import { OAI_REALTIME_EVENT, IOaiRealtimeEvent } from 'services/alpha/oaiRealtimeEvents';

export const CANCELLED_ITEM_PREFIX = 'local-cancelled-';

export const CAPTION_MAX_CHARS = 140;

export interface IAlphaVoiceLiveItem {
  itemId: string;
  role: 'user' | 'assistant';
  text: string;
  final: boolean;
}

const roleFor = (type: string): 'user' | 'assistant' | null => {
  if (
    type === OAI_REALTIME_EVENT.USER_TRANSCRIPT_DELTA ||
    type === OAI_REALTIME_EVENT.USER_TRANSCRIPT_DONE
  ) {
    return 'user';
  }
  if (
    type === OAI_REALTIME_EVENT.ASSISTANT_TRANSCRIPT_DELTA ||
    type === OAI_REALTIME_EVENT.ASSISTANT_TRANSCRIPT_DONE
  ) {
    return 'assistant';
  }
  return null;
};

const isDone = (type: string): boolean =>
  type === OAI_REALTIME_EVENT.USER_TRANSCRIPT_DONE ||
  type === OAI_REALTIME_EVENT.ASSISTANT_TRANSCRIPT_DONE;

/**
 * Folds one realtime transcript event into the live-item list, keyed by
 * item_id so repeated deltas accumulate into ONE bubble and a trailing
 * '.done' event REPLACES the accumulated text with the authoritative final
 * transcript (delta concatenation can drift from it) instead of appending a
 * second bubble for the same item - this is the "don't render twice" dedupe.
 */
export function applyVoiceTranscriptEvent (
  items: IAlphaVoiceLiveItem[],
  event: IOaiRealtimeEvent,
): IAlphaVoiceLiveItem[] {
  const role = roleFor(event.type);
  if (!role || !event.item_id) return items;

  const index = items.findIndex(item => item.itemId === event.item_id);
  const done = isDone(event.type);
  const text = done ? event.transcript ?? '' : event.delta ?? '';

  if (index === -1) {
    return [...items, { itemId: event.item_id, role, text, final: done }];
  }

  const next = [...items];
  next[index] = {
    ...next[index],
    text: done ? text : next[index].text + text,
    final: done || next[index].final,
  };
  return next;
}

export function captionWindow (text: string, maxChars = CAPTION_MAX_CHARS): string {
  if (text.length <= maxChars) return text;
  const start = text.length - maxChars;
  const tail = text.slice(start);
  if (text[start - 1] === ' ') return `…${tail}`;
  const firstSpace = tail.indexOf(' ');
  return `…${firstSpace === -1 ? tail : tail.slice(firstSpace + 1)}`;
}

export function pickCaptionItem (items: IAlphaVoiceLiveItem[]): IAlphaVoiceLiveItem | undefined {
  const streaming = [...items].reverse().find(item => !item.final);
  if (streaming) return streaming;
  const assistant = [...items].reverse().find(item => item.role === 'assistant');
  return assistant ?? items[items.length - 1];
}
